import { useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { UserRound } from "lucide-react";
import { Menu, X, Search } from "lucide-react";
import { Marquee } from "@/components/Marquee";
import { PRODUCTS } from "@/data/catalog";
import { chooseLanguage } from "@/i18n";
import { SocialLoginButtons } from "@/components/SocialLogin";

const Flag = ({ id, size = 22 }) => {
  const r = size / 2;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" className="rounded-full shrink-0" style={{ boxShadow: "0 0 0 1px rgba(255,255,255,.25)" }}>
      <defs><clipPath id={`flag-${id}-${size}`}><circle cx="12" cy="12" r="12" /></clipPath></defs>
      <g clipPath={`url(#flag-${id}-${size})`}>
        {id === "it" && <><rect width="8" height="24" fill="#009246" /><rect x="8" width="8" height="24" fill="#fff" /><rect x="16" width="8" height="24" fill="#CE2B37" /></>}
        {id === "ro" && <><rect width="8" height="24" fill="#002B7F" /><rect x="8" width="8" height="24" fill="#FCD116" /><rect x="16" width="8" height="24" fill="#CE1126" /></>}
        {id === "en" && <>
          <rect width="24" height="24" fill="#012169" />
          <path d="M0 0L24 24M24 0L0 24" stroke="#fff" strokeWidth="4.5" />
          <path d="M0 0L24 24M24 0L0 24" stroke="#C8102E" strokeWidth="2" />
          <path d="M12 0V24M0 12H24" stroke="#fff" strokeWidth="7" />
          <path d="M12 0V24M0 12H24" stroke="#C8102E" strokeWidth="4" />
        </>}
        {id === "de" && <><rect width="24" height="8" fill="#000" /><rect y="8" width="24" height="8" fill="#DD0000" /><rect y="16" width="24" height="8" fill="#FFCE00" /></>}
        {id === "fr" && <><rect width="8" height="24" fill="#0055A4" /><rect x="8" width="8" height="24" fill="#fff" /><rect x="16" width="8" height="24" fill="#EF4135" /></>}
        {id === "es" && <><rect width="24" height="6" fill="#AA151B" /><rect y="6" width="24" height="12" fill="#F1BF00" /><rect y="18" width="24" height="6" fill="#AA151B" /></>}
        {id === "pt" && <><rect width="9.6" height="24" fill="#006600" /><rect x="9.6" width="14.4" height="24" fill="#FF0000" /><circle cx="9.6" cy="12" r="4" fill="#FFCC00" stroke="#003399" strokeWidth="1" /></>}
        {id === "nl" && <><rect width="24" height="8" fill="#AE1C28" /><rect y="8" width="24" height="8" fill="#fff" /><rect y="16" width="24" height="8" fill="#21468B" /></>}
        {id === "pl" && <><rect width="24" height="12" fill="#fff" /><rect y="12" width="24" height="12" fill="#DC143C" /></>}
        {id === "el" && <><rect width="24" height="24" fill="#0D5EAF" />{[2.7, 8, 13.3, 18.7].map((y) => <rect key={y} y={y} width="24" height="2.7" fill="#fff" />)}<rect width="10.7" height="10.7" fill="#0D5EAF" /><path d="M5.3 0V10.7M0 5.3H10.7" stroke="#fff" strokeWidth="2.7" /></>}
        {id === "sv" && <><rect width="24" height="24" fill="#006AA7" /><path d="M8 0V24M0 12H24" stroke="#FECC00" strokeWidth="4" /></>}
        {id === "zh" && <><rect width="24" height="24" fill="#DE2910" /><polygon points="6,4 7.3,7.6 11,7.6 8,9.8 9.1,13.4 6,11.2 2.9,13.4 4,9.8 1,7.6 4.7,7.6" fill="#FFDE00" /></>}
        {id === "ru" && <><rect width="24" height="8" fill="#fff" /><rect y="8" width="24" height="8" fill="#0039A6" /><rect y="16" width="24" height="8" fill="#D52B1E" /></>}
        {id === "uk" && <><rect width="24" height="12" fill="#0057B7" /><rect y="12" width="24" height="12" fill="#FFD700" /></>}
      </g>
      <circle cx="12" cy="12" r={r - 0.5} fill="none" stroke="rgba(0,0,0,.25)" />
    </svg>
  );
};

const LANGS = [
  { id: "it", label: "Italiano" },
  { id: "en", label: "English" },
  { id: "ro", label: "Română" },
  { id: "de", label: "Deutsch" },
  { id: "fr", label: "Français" },
  { id: "es", label: "Español" },
  { id: "pt", label: "Português" },
  { id: "nl", label: "Nederlands" },
  { id: "pl", label: "Polski" },
  { id: "el", label: "Ελληνικά" },
  { id: "sv", label: "Svenska" },
  { id: "zh", label: "中文" },
  { id: "ru", label: "Русский" },
  { id: "uk", label: "Українська" },
];

export const LanguageSwitcher = () => {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const current = LANGS.some((l) => l.id === i18n.language?.slice(0, 2)) ? i18n.language.slice(0, 2) : "it";
  return (
    <div className="relative" data-testid="language-switcher" onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setOpen(false); }}>
      <button onClick={() => setOpen(!open)} aria-label={t("nav.lang")} title={t("nav.lang")} aria-expanded={open} data-testid="lang-toggle"
        className="w-9 h-9 rounded-full flex items-center justify-center border border-white/15 hover:border-[#D4AF37] transition-colors bg-white/[0.04]">
        <Flag id={current} size={22} />
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-44 max-h-[70vh] overflow-y-auto bg-[#111] border border-[#D4AF37]/30 rounded-2xl shadow-2xl z-50 py-1" data-testid="lang-menu">
          {LANGS.map((l) => (
            <button key={l.id} onClick={() => { chooseLanguage(l.id); setOpen(false); }} data-testid={`lang-${l.id}`}
              className={`w-full flex items-center gap-2.5 px-3 py-2 text-xs font-semibold transition-colors ${current === l.id ? "text-[#D4AF37] bg-white/[0.04]" : "text-white/75 hover:bg-white/[0.06] hover:text-white"}`}>
              <Flag id={l.id} size={18} /> {l.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const SearchBar = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [focus, setFocus] = useState(false);
  const ref = useRef(null);

  const sections = [
    { label: t("nav.products"), href: "/#prodotti" },
    { label: t("nav.how"), href: "/#come-funziona" },
    { label: t("nav.pricing"), href: "/prezzi" },
    { label: t("nav.sectors"), href: "/#settori" },
    { label: t("nav.manifesto"), href: "/#manifesto" },
    { label: t("nav.faq"), href: "/#faq" },
    { label: t("testimonials.eyebrow"), href: "/#testimonianze" },
  ];

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 2) return [];
    const productHits = PRODUCTS.filter((p) => !p.hidden)
      .filter((p) => t(`prodData.${p.id}.name`).toLowerCase().includes(q) || t(`prodData.${p.id}.variant`).toLowerCase().includes(q))
      .map((p) => ({ label: `${t(`prodData.${p.id}.name`)} — ${t(`prodData.${p.id}.variant`)}`, go: `/configura?prodotto=${p.id}`, type: "product" }));
    const sectionHits = sections
      .filter((s) => s.label.toLowerCase().includes(q))
      .map((s) => ({ label: s.label, go: s.href, type: "section" }));
    return [...productHits, ...sectionHits].slice(0, 6);
  }, [query]); // eslint-disable-line react-hooks/exhaustive-deps

  const go = (r) => {
    setQuery("");
    setFocus(false);
    if (r.type === "product") navigate(r.go);
    else window.location.href = r.go;
  };

  return (
    <div className="relative hidden sm:block" ref={ref}>
      <span className="absolute inset-y-0 left-3 flex items-center pointer-events-none text-white/40">
        <Search className="w-3.5 h-3.5" />
      </span>
      <input
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => setFocus(true)}
        onBlur={() => setTimeout(() => setFocus(false), 150)}
        placeholder={t("nav.searchPh")}
        data-testid="nav-search-input"
        className="pl-8 pr-3 py-2 bg-white/[0.06] border border-white/10 rounded-full text-xs text-white placeholder:text-white/40 focus:outline-none focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/25 w-40 2xl:w-44 focus:w-56 transition-[width,border-color]"
      />
      {focus && query.trim().length >= 2 && (
        <div className="absolute right-0 mt-2 w-72 bg-[#111] border border-[#D4AF37]/30 rounded-2xl shadow-2xl overflow-hidden z-50" data-testid="nav-search-results">
          {results.length === 0 && <p className="px-4 py-3 text-xs text-white/40">{t("nav.searchEmpty")}</p>}
          {results.map((r, i) => (
            <button key={i} onClick={() => go(r)} data-testid={`nav-search-result-${i}`}
              className="w-full text-left px-4 py-2.5 text-xs font-medium text-white/80 hover:bg-white/[0.06] hover:text-[#D4AF37] transition-colors flex items-center justify-between">
              <span>{r.label}</span>
              <span className="text-[9px] uppercase tracking-wider text-[#D4AF37]/70">{r.type === "product" ? t("nav.products") : "§"}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const { user } = useAuth();

  const links = [
    { id: "account", label: user ? user.name.split(" ")[0] : t("nav.login"), mobileLabel: user ? null : t("nav.loginRegister"), to: user ? "/account" : "/accedi", icon: UserRound },
    { id: "prodotti", label: t("nav.products"), href: "/#prodotti" },
    { id: "come-funziona", label: t("nav.how"), href: "/#come-funziona" },
    { id: "settori", label: t("nav.sectors"), href: "/#settori" },
    { id: "prezzi", label: t("nav.pricing"), to: "/prezzi" },
    { id: "configura", label: t("nav.cta"), to: "/configura", gold: true },
    { id: "manifesto", label: t("nav.manifesto"), href: "/#manifesto" },
    { id: "faq", label: t("nav.faq"), href: "/#faq" },
  ];
  // Menu mobile: Accedi → login social → Personalizza → sezioni → Manifesto → FAQ
  const mobileOrder = ["prodotti", "prezzi", "come-funziona", "settori", "manifesto", "faq", "account"];
  const mobileLinks = mobileOrder.map((id) => links.find((l) => l.id === id));

  const NavItem = ({ l, mobile }) => {
    const cls = mobile
      ? `flex items-center gap-2 text-sm font-semibold py-1 ${l.gold ? "text-[#D4AF37]" : "text-white/80 hover:text-[#D4AF37]"}`
      : `inline-flex items-center gap-1.5 text-[13px] font-semibold whitespace-nowrap transition-colors ${l.gold ? "text-[#D4AF37] hover:text-[#E6C766]" : "text-white/70 hover:text-[#D4AF37]"}`;
    const testId = mobile ? `nav-mobile-${l.id}` : `nav-link-${l.id}`;
    const inner = <>{l.icon && <l.icon className="w-3.5 h-3.5" />}{mobile && l.mobileLabel ? l.mobileLabel : l.label}</>;
    return l.to
      ? <Link to={l.to} onClick={() => setOpen(false)} data-testid={testId} className={cls}>{inner}</Link>
      : <a href={l.href} onClick={() => setOpen(false)} data-testid={testId} className={cls}>{inner}</a>;
  };

  return (
    <div className="fixed top-0 inset-x-0 z-50">
      <Marquee />
      <header className="backdrop-blur-xl bg-[#0b0b0b]/85 border-b border-[#D4AF37]/20 text-white" data-testid="main-navbar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2.5 shrink-0" data-testid="nav-logo">
            <img src="/assets/if-card-logo.webp" alt="InfinityFeedback" className="h-11 w-[77px] rounded-md object-cover ring-1 ring-[#D4AF37]/50 shadow-[0_2px_10px_rgba(212,175,55,0.3)]" />
            <span className="font-display text-lg tracking-tight text-white font-extrabold whitespace-nowrap">
              Infinity<span className="text-[#D4AF37]">Feedback</span>
            </span>
          </Link>

          <nav className="hidden xl:flex items-center gap-5 2xl:gap-7" data-testid="nav-desktop">
            {links.filter((l) => l.id !== "account").map((l) => <NavItem key={l.id} l={l} />)}
          </nav>

          <div className="flex items-center gap-2.5">
            <SearchBar />
            <LanguageSwitcher />
            <Link to={user ? "/account" : "/accedi"} className="nav-account-access" data-testid="nav-link-account"><UserRound size={17} /><span>{user ? "Account" : t("nav.login")}</span></Link>
            <button className="xl:hidden p-2 text-white" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle" aria-label="Menu">
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {open && (
          <div className="xl:hidden border-t border-[#D4AF37]/20 bg-[#0b0b0b] px-6 py-4 space-y-2.5" data-testid="nav-mobile-menu">
            {mobileLinks.map((l) => (
              <div key={l.id} className={l.id === "account" ? "pt-3 mt-1 border-t border-[#D4AF37]/20 space-y-3" : "space-y-2.5"}>
                <NavItem l={l} mobile />
                {l.id === "account" && !user && <SocialLoginButtons compact testPrefix="nav-social" />}
              </div>
            ))}
          </div>
        )}
      </header>
    </div>
  );
};
